import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const LINES = ["Tu sonrisa.", "Tu hogar.", "Nuestro cuidado."];
const easeOut = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-8">
        {/* Overline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="col-span-12 text-xs md:text-sm uppercase tracking-[0.28em] text-[color:var(--text-secondary)] flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="inline-block w-8 h-px bg-[color:var(--text-secondary)]" />
          Nº 01 · Higiene dental a domicilio · Madrid
        </motion.p>

        {/* Headline */}
        <h1
          className="col-span-12 md:col-span-8 font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.92] mt-8"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.14, ease: easeOut }}
              >
                {i === 2 ? (
                  <>
                    Nuestro <em className="italic text-[color:var(--accent)]">cuidado.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Image with parallax + arch clip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.8, ease: easeOut }}
          className="col-span-12 md:col-span-4 relative h-[380px] md:h-[520px] rounded-t-[240px] rounded-b-2xl overflow-hidden bg-[color:var(--bg-secondary)]"
          data-testid="hero-image"
        >
          <motion.img
            src="https://customer-assets.emergentagent.com/job_inhoa-at-home/artifacts/scu0tt3r_IMG-20260708-WA0014.jpg"
            alt="Dentainhoa – Inhoa"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 w-full h-full object-contain p-6"
          />
          <div className="absolute inset-0 bg-[color:var(--signature-dark)]/10" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[color:var(--bg-primary)] text-xs font-mono">
            <span>MAD · 40.42° N</span>
            <span>ES · 2025</span>
          </div>
        </motion.div>

        {/* Sublead */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: easeOut }}
          className="col-span-12 md:col-span-6 md:col-start-1 mt-8 md:mt-14 flex flex-col gap-6"
        >
          <p className="font-heading text-2xl md:text-3xl leading-tight text-[color:var(--text-primary)] max-w-lg">
            Higiene Dental Profesional a domicilio.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-[color:var(--text-secondary)] max-w-lg">
            Cuidamos de tu salud bucodental con un trato cercano, atención personalizada y el máximo compromiso profesional.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-[color:var(--text-secondary)] max-w-lg">
            Confía en nosotros para ofrecerte un servicio de calidad en cada visita.
          </p>
          <p className="italic text-[color:var(--accent)] text-lg md:text-xl max-w-lg">
            Sonríe con tranquilidad. Nosotros nos encargamos del resto.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <a
              href="#cita"
              onClick={(e) => { e.preventDefault(); document.querySelector("#cita")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-3 rounded-full bg-[color:var(--accent)] text-white px-7 py-3.5 text-sm hover:bg-[color:var(--accent-hover)] transition-colors"
            >
              Reservar mi cita
              <span aria-hidden>→</span>
            </a>
            <a
              href="#manifiesto"
              onClick={(e) => { e.preventDefault(); document.querySelector("#manifiesto")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="hero-cta-secondary"
              className="text-sm tracking-wide underline underline-offset-4 text-[color:var(--text-primary)] hover:text-[color:var(--accent)]"
            >
              Descubrir el método
            </a>
          </div>
        </motion.div>

        {/* Meta right */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="col-span-12 md:col-span-4 md:col-start-9 mt-8 md:mt-14 flex md:flex-col gap-6 font-mono text-xs uppercase tracking-widest text-[color:var(--text-secondary)]"
        >
          <div>
            <div className="text-[color:var(--text-primary)] text-3xl font-heading normal-case tracking-tight">+2 años</div>
            Experiencia en Bucodental
          </div>
          <div>
            <div className="text-[color:var(--text-primary)] text-3xl font-heading normal-case tracking-tight">100%</div>
            ATENCIÓN CERCANA Y PERSONALIZADA
          </div>
          <div>
            <div className="text-[color:var(--text-primary)] text-3xl font-heading normal-case tracking-tight leading-none">✓</div>
            protocolo esterilización
          </div>
        </motion.div>
      </div>
    </section>
  );
}
